// src/services/aiChatService.ts
import axios from "axios";

const API_URL =
  (import.meta as any).env?.VITE_API_URL || "http://localhost:5000/api";

export interface ChatMessage {
  role: "user" | "assistant" | "system";
  content: string;
}

export interface MenuItemForAI {
  id: string;
  name: string;
  price: number;
  category: string;
  spicy: boolean;
  veg: boolean;
  description?: string;
}

class AIChatService {
  private conversationHistory: ChatMessage[] = [];
  private sessionId: string;
  private cachedMenu: MenuItemForAI[] | null = null;
  private cachedTopSelling: any[] | null = null;

  constructor() {
    this.sessionId =
      localStorage.getItem("chat_session_id") || this.generateSessionId();
    localStorage.setItem("chat_session_id", this.sessionId);
  }

  private generateSessionId(): string {
    return (
      "session_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9)
    );
  }

  async getMenuData(restaurantId: string): Promise<MenuItemForAI[]> {
    // Return cached menu if available
    if (this.cachedMenu) {
      return this.cachedMenu;
    }

    try {
      const response = await axios.get(
        `${API_URL}/menu/public/${restaurantId}`,
      );
      if (response.data.success && response.data.data) {
        const menuData = response.data.data;
        const items: MenuItemForAI[] = [];

        menuData.menus.categories.forEach((category: any) => {
          category.items.forEach((item: any) => {
            items.push({
              id: item.id,
              name: item.name,
              price: item.price,
              category: category.name,
              spicy:
                item.name.toLowerCase().includes("spicy") ||
                item.name.toLowerCase().includes("chilli") ||
                item.description?.toLowerCase().includes("spicy") ||
                false,
              veg: item.isVegetarian,
              description: item.description,
            });
          });
        });

        this.cachedMenu = items;
        console.log(`Loaded ${items.length} menu items for AI`);
        return items;
      }
      return [];
    } catch (error) {
      console.error("Error fetching menu for AI:", error);
      return [];
    }
  }

  async getTopSellingItems(restaurantId: string): Promise<any[]> {
    if (this.cachedTopSelling) {
      return this.cachedTopSelling;
    }

    try {
      const response = await axios.get(
        `${API_URL}/analytics/top-selling/${restaurantId}`,
      );
      if (response.data.success) {
        this.cachedTopSelling = response.data.data;
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error("Error fetching top selling items:", error);
      return [];
    }
  }

  // Update the sendMessage method
  async sendMessage(message: string, restaurantId: string): Promise<string> {
    try {
      this.conversationHistory.push({ role: "user", content: message });

      const response = await axios.post(`${API_URL}/chat`, {
        message,
        sessionId: this.sessionId,
        restaurantId,
      });

      if (response.data.success && response.data.reply) {
        this.conversationHistory.push({
          role: "assistant",
          content: response.data.reply,
        });
        return response.data.reply;
      }

      throw new Error("No response from AI");
    } catch (error) {
      console.error("AI Chat Service Error:", error);
      return this.getFallbackResponse(message);
    }
  }

  private getFallbackResponse(userMessage: string): string {
    const lowerMessage = userMessage.toLowerCase();

    const responses: Record<string, string> = {
      popular:
        "🔥 **Our Most Popular Items:**\n\n• Buff MOMO - Rs. 120\n• Chicken MOMO - Rs. 150\n• Veg MOMO - Rs. 100\n• Milk Tea - Rs. 30\n• Chicken Chili - Rs. 170\n\nThese are customer favorites!",
      veg: "🌱 **Vegetarian Options:**\n\n• Veg MOMO - Rs. 100\n• Veg Fried Rice - Rs. 120\n• Veg Chowmein - Rs. 100\n• Paneer Curry - Rs. 180\n• Veg C MOMO - Rs. 150",
      spicy:
        "🌶️ **Spicy Items:**\n\n• Chicken Chilli MOMO - Rs. 200\n• Spicy Buff Fry - Rs. 170\n• Hot Garlic Chicken - Rs. 190\n• Buff C MOMO - Rs. 170\n• Chicken C MOMO - Rs. 200",
      momo: "🥟 **MOMO Varieties:**\n\n• Buff MOMO - Rs. 120\n• Chicken MOMO - Rs. 150\n• Veg MOMO - Rs. 100\n• Buff Fry MOMO - Rs. 150\n• Chicken Fry MOMO - Rs. 170\n• Veg Fry MOMO - Rs. 120",
    };

    for (const [key, response] of Object.entries(responses)) {
      if (lowerMessage.includes(key)) {
        return response;
      }
    }

    return '🍽️ **Welcome to Our Restaurant!**\n\nI can help you with:\n• **Popular items** - Ask "What are your best sellers?"\n• **Vegetarian options** - "Show me vegetarian items"\n• **Specific item prices** - "Price of Buff MOMO"\n• **Spicy dishes** - "What spicy items do you have?"\n• **MOMO varieties** - "Show me MOMO items"\n\nWhat would you like to know about our menu?';
  }

  clearHistory(): void {
    this.conversationHistory = [];
    this.sessionId = this.generateSessionId();
    localStorage.setItem("chat_session_id", this.sessionId);
  }

  getHistory(): ChatMessage[] {
    return [...this.conversationHistory];
  }
}

export const aiChatService = new AIChatService();
